import type React from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { ROLES } from "@infinitaslearning/module-he-common";
import {
  AdaptiveWorkflowSelectPage,
  AdminPage,
  CreateBasicAccountPage,
  DiagnosticTestExamPage,
  InvitationPage,
  LandingPage,
  LoginValidatePage,
  ProductsOwnedPage,
  RegistrationPage,
  ResultsPage,
  SchoolDataRequestPage,
  SignInPage,
  SkillDeterminationTestPage,
  StudentDashboard,
  TeacherDashboard,
  TeacherMonitor,
  TestPage,
  WritingExercisePage,
} from "pages";
import { AccountCreatedPage } from "pages/Auth/AccountCreatedPage/AccountCreatedPage";
import { ScheduledTestResultsDetails } from "pages/TeacherEnvironment/TestResultsReport/TestResultsReport";
import type { ROLE } from "persistence";

import { RoleComponent } from "layouts/RoleComponent";

import { BlockedPathMessage } from "components/BlockedPathMessage/BlockedPathMessage";
import { NotFoundPathMessage } from "components/NotFoundPathMessage/NotFoundPathMessage";
import { UnderConstructionMessage } from "components/UnderConstructionMessage/UnderConstructionMessage";
import { TestType } from "constants/exam-constants";
import {
  ADMIN_ENVIRONMENT_ROUTES,
  AUTH_PAGES_ROUTES,
  GENERIC_ROUTES,
  LEGACY_ROUTES,
  MISC_PAGE_ROUTES,
  PRODUCT_PAGES_ROUTES,
  RESULT_PAGE_ROUTES,
  TEACHER_HOME_PAGE_ROUTES,
  TEACHER_MONITOR_PAGE_ROUTES,
  TEACHER_SCHEDULED_TEST_PAGE_ROUTES,
  TEST_ENVIRONMENT_ROUTES,
  TEST_PAGE_ROUTES,
} from "constants/routes";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import {
  NewUserPage,
  SchoolDetailsPage,
  SchoolsPage,
  TeacherInstructionsAdminPage,
  UserDetailsPage,
  UserScheduledTests,
  UserScheduledTestsGroup,
  UserToolsPage,
  UsersPage,
} from "pages/AdminEnvironment/AdminPage/subpages";
import { OAuthAccountLinkPage } from "pages/Auth";
import { AccessDeniedPage } from "pages/Auth/AccessDeniedPage/AccessDeniedPage";
import { RegistrationSuccessfulPage } from "pages/Auth/RegistrationSuccessfulPage/RegistrationSuccessfulPage";
import {
  AdaptivePracticeSessionResultsPage,
  CertificateTestResultsPage,
  DiagnosticTestDetailsPage,
  DiagnosticTestGroupsPage,
  DiagnosticTestListPage,
  EdifyModuleDetailsPage,
  EdifySubjectDetailsPage,
  EdifyTopicDetailsPage,
  ExpiredLicensePage,
  FormativeTestResultsPage,
  FreebieTestResultsPage,
  ModuleDetailsPage,
  ModuleDetailsV2Page,
  NoLicensePage,
  StudentDashboardV2Page,
  SummativeTestResultsPage,
  TopicDetailsPage,
  WritingSubjectDetailsPage,
} from "pages/StudentEnvironment/StudentDashboard/subpages";
import { TeacherHomeRedirect } from "pages/TeacherEnvironment/TeacherDashboard/components/TeacherHomeRedirect/TeacherHomeRedirect";
import {
  BasicProgressForAccountPage,
  BasicProgressOverviewPage,
  EditAccountPage,
  GroupDetailsPage,
  GroupListPage,
  InviteManagementPage,
  LiveMonitorOverviewPage,
  OwnedScheduledTestResultsOverviewPage,
  ScheduledTestRequestPage,
  ScheduledTestResultsForAccountPage,
  ScheduledTestResultsStudent,
  ScheduledTestsPage,
  StudentLicense,
  WritingResultsForAccount,
  WritingResultsOverviewPage,
} from "pages/TeacherEnvironment/TeacherDashboard/subpages";
import {
  InvitationBundleList,
  InvitedStudentList,
} from "pages/TeacherEnvironment/TeacherDashboard/subpages/InviteManagement";
import { CreateInviteForm } from "pages/TeacherEnvironment/TeacherDashboard/subpages/StudentLicense/subpages/CreateInviteForm/CreateInviteForm";
import { InvitationList } from "pages/TeacherEnvironment/TeacherDashboard/subpages/StudentLicense/subpages/InvitationList/InvitationList";
import { TeacherInstructions } from "pages/TeacherEnvironment/TeacherDashboard/subpages/TeacherInstructions/TeacherInstructions";
import * as TestEnvPages from "pages/TestEnvironment/TestPage/subpages";

import { ClaimInviteLinkLicensePage } from "pages/StudentEnvironment";
import { JoinGroupPage } from "pages/StudentEnvironment/JoinGroupPage/JoinGroupPage";
import { ComingSoonModulesPage } from "pages/StudentEnvironment/StudentDashboard/subpages/ComingSoonModulesPage/ComingSoonModules";
import { ExerciseResultsPage, TestResultsPage } from "pages/TestEnvironment/ResultsPage/subpages";

import { AdaptiveTestPageRenderer } from "./AdaptiveTestPageRenderer";
import { RootPath } from "./RootPath";
import AppRouteWrapper from "./RouteWrappers/AppRouteWrapper";
import PrivateRouteWrapper from "./RouteWrappers/PrivateRouteWrapper";

const AppRouter: () => React.ReactElement = () => {
  const {
    showAdaptivePracticeFlow,
    showComingSoonModules,
    showSubjectsGroupedByModuleSection,
    showRedesignedEmailInvitePages,
    enabledLinkInvitesFlag,
  } = useFeatureFlags();

  return (
    <Routes>
      <Route element={<AppRouteWrapper />}>
        {/* ---------- PUBLIC ROUTES ---------- */}

        {/* -- Generic routes --- */}
        <Route path={GENERIC_ROUTES.NOT_FOUND} element={<NotFoundPathMessage />} />
        <Route path={GENERIC_ROUTES.BLOCKED_PATH} element={<BlockedPathMessage />} />

        {/* --- Authentication pages --- */}
        <Route path={AUTH_PAGES_ROUTES.LOG_IN} element={<SignInPage />} />
        <Route path={AUTH_PAGES_ROUTES.VALIDATE} element={<LoginValidatePage />} />
        <Route path={AUTH_PAGES_ROUTES.REGISTER_ACCOUNT} element={<RegistrationPage />} />
        <Route
          path={AUTH_PAGES_ROUTES.REGISTER_ACCOUNT_FINISHED}
          element={<RegistrationSuccessfulPage />}
        />
        <Route path={AUTH_PAGES_ROUTES.CREATE_BASIC_ACCOUNT} element={<CreateBasicAccountPage />} />
        <Route path={AUTH_PAGES_ROUTES.REGISTERED_INVITATION} element={<AccountCreatedPage />} />
        <Route path={AUTH_PAGES_ROUTES.INVITATION} element={<InvitationPage />} />
        <Route path={AUTH_PAGES_ROUTES.ACCESS_DENIED} element={<AccessDeniedPage />} />
        <Route path={AUTH_PAGES_ROUTES.OAUTH_ACCOUNT_LINK} element={<OAuthAccountLinkPage />} />

        {/* ---------- PRIVATE ROUTES ---------- */}
        <Route element={<PrivateRouteWrapper />}>
          {/* --- Default route / Root path --- */}
          <Route index element={<RootPath />} />

          {/* --- Account activation --- */}
          <Route path={AUTH_PAGES_ROUTES.USER_MISSING_INFO} element={<SchoolDataRequestPage />} />

          {/* -- Join group page (for students only) -- */}
          <Route
            path={MISC_PAGE_ROUTES.JOIN_GROUP}
            element={
              <RoleComponent
                allowedRoles={[ROLES.STUDENT as ROLE]}
                fallbackContent={<Navigate to={GENERIC_ROUTES.HOME_PAGE} />}
              >
                <JoinGroupPage />
              </RoleComponent>
            }
          />

          {/* -- Claim license via invite link page (for students only) -- */}
          {enabledLinkInvitesFlag && (
            <Route
              path={MISC_PAGE_ROUTES.CLAIM_LINK_INVITE_LICENSE}
              element={
                <RoleComponent
                  allowedRoles={[ROLES.STUDENT as ROLE]}
                  fallbackContent={<Navigate to={GENERIC_ROUTES.HOME_PAGE} />}
                >
                  <ClaimInviteLinkLicensePage />
                </RoleComponent>
              }
            />
          )}

          {/* --- Student pages --- */}
          <Route path={PRODUCT_PAGES_ROUTES.PRODUCT_LIST} element={<ProductsOwnedPage />} />

          <Route path={RESULT_PAGE_ROUTES.OBTAINED_CERTIFICATES} element={<StudentDashboard />}>
            <Route index Component={CertificateTestResultsPage} />
          </Route>

          <Route path={PRODUCT_PAGES_ROUTES.NO_PRODUCT_LICENSE} element={<StudentDashboard />}>
            <Route index Component={NoLicensePage} />
          </Route>

          <Route path="/product/results" element={<StudentDashboard />}>
            <Route path={TestType.SUMMATIVE_TEST} Component={SummativeTestResultsPage} />
            <Route path={TestType.FORMATIVE_TEST} Component={FormativeTestResultsPage} />
            <Route path={TestType.INTAKE_TEST} Component={FreebieTestResultsPage} />
          </Route>

          <Route path={PRODUCT_PAGES_ROUTES.PRODUCT_DETAILS} element={<StudentDashboard />}>
            <Route index Component={StudentDashboardV2Page} />
            <Route path="expired-license" Component={ExpiredLicensePage} />
            <Route
              path="module/:moduleId"
              Component={
                !showSubjectsGroupedByModuleSection ? ModuleDetailsPage : ModuleDetailsV2Page
              }
            />
            <Route
              path="module/:moduleId/subjects/:subjectId"
              Component={WritingSubjectDetailsPage}
            />
            <Route
              path="module/:moduleId/subjects/:subjectId/topic/:topicId"
              Component={TopicDetailsPage}
            />
            <Route
              path="module/:moduleId/diagnostic-test/groups"
              Component={DiagnosticTestGroupsPage}
            />
            <Route
              path="module/:moduleId/diagnostic-test/groups/:groupId"
              Component={DiagnosticTestListPage}
            />
            <Route
              path="module/:moduleId/diagnostic-test/groups/:groupId/tests/:testId"
              Component={DiagnosticTestDetailsPage}
            />
            <Route
              path="coming-soon/module/:moduleType"
              element={
                showComingSoonModules ? (
                  <ComingSoonModulesPage />
                ) : (
                  <Navigate to={GENERIC_ROUTES.HOME_PAGE} />
                )
              }
            />

            {/* -- Edify content routes --- */}
            <Route path="edify-module/:packageId" Component={EdifyModuleDetailsPage} />
            <Route
              path="edify-module/:packageId/subject/:chapterId"
              Component={EdifySubjectDetailsPage}
            />
            <Route
              path="edify-module/:packageId/subject/:chapterId/topic/:contentUnitId"
              Component={EdifyTopicDetailsPage}
            />
          </Route>

          <Route
            path={TEST_PAGE_ROUTES.ADAPTIVE_PRACTICE_TEST}
            element={
              showAdaptivePracticeFlow ? <AdaptiveTestPageRenderer /> : <UnderConstructionMessage />
            }
          />

          <Route
            path={TEST_PAGE_ROUTES.ADAPTIVE_PRACTICE_WORKFLOW_SELECT}
            element={<AdaptiveWorkflowSelectPage />}
          />

          <Route
            path={TEST_PAGE_ROUTES.ADAPTIVE_PRACTICE_SESSION_RESULTS}
            element={<AdaptivePracticeSessionResultsPage />}
          />

          <Route
            path={TEST_PAGE_ROUTES.SKILL_DETERMINATION_TEST}
            element={
              showAdaptivePracticeFlow ? (
                <SkillDeterminationTestPage />
              ) : (
                <UnderConstructionMessage />
              )
            }
          />

          <Route
            path={TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_EXAM}
            element={<DiagnosticTestExamPage />}
          />

          <Route path={TEST_PAGE_ROUTES.WRITING_EXERCISE} element={<WritingExercisePage />} />

          {/* --- Teacher environment --- */}
          <Route
            path={TEACHER_MONITOR_PAGE_ROUTES.SCHEDULED_TEST_LIVE_MONITOR}
            element={
              <RoleComponent
                allowedRoles={[ROLES.TEACHER as ROLE]}
                fallbackContent={<Navigate to={GENERIC_ROUTES.HOME_PAGE} />}
              >
                <TeacherMonitor />
              </RoleComponent>
            }
          />

          <Route
            path={TEACHER_SCHEDULED_TEST_PAGE_ROUTES.SCHEDULED_TEST_RESULTS}
            element={
              <RoleComponent
                allowedRoles={[ROLES.TEACHER as ROLE]}
                fallbackContent={<Navigate to={GENERIC_ROUTES.HOME_PAGE} />}
              >
                <ScheduledTestResultsDetails />
              </RoleComponent>
            }
          />

          <Route
            path={TEACHER_HOME_PAGE_ROUTES.HOME_PAGE}
            element={
              <RoleComponent
                allowedRoles={[ROLES.TEACHER as ROLE]}
                fallbackContent={<Navigate to={GENERIC_ROUTES.HOME_PAGE} />}
              >
                <TeacherDashboard />
              </RoleComponent>
            }
          >
            <Route index Component={TeacherHomeRedirect} />
            <Route
              path="insights/product/:productId/progress"
              Component={BasicProgressOverviewPage}
            />
            {showRedesignedEmailInvitePages ? (
              <Route path="invite" Component={InviteManagementPage}>
                <Route
                  index
                  element={<Navigate to={TEACHER_HOME_PAGE_ROUTES.SENT_INVITE_BUNDLE_LIST} />}
                />
                <Route path="bundles" Component={InvitationBundleList} />
                <Route path="list" Component={InvitedStudentList} />
              </Route>
            ) : (
              <Route path="invite" Component={StudentLicense}>
                <Route index element={<Navigate to={TEACHER_HOME_PAGE_ROUTES.CREATE_INVITE} />} />
                <Route path="new" Component={CreateInviteForm} />
                <Route path="list" Component={InvitationList} />
              </Route>
            )}
            <Route
              path="insights/product/:productId/progress/account/:accountId"
              Component={BasicProgressForAccountPage}
            />
            <Route
              path="insights/product/:productId/writing"
              Component={WritingResultsOverviewPage}
            />
            <Route
              path="insights/product/:productId/writing/results/account/:accountId"
              Component={WritingResultsForAccount}
            />
            <Route
              path="insights/product/:productId/account/:accountId/edit"
              Component={EditAccountPage}
            />
            <Route path="scheduled-tests/created" Component={ScheduledTestsPage} />
            <Route path="scheduled-tests/created/new" Component={ScheduledTestRequestPage} />
            <Route
              path="scheduled-tests/created/edit/:scheduledTestId"
              Component={ScheduledTestRequestPage}
            />
            <Route path="scheduled-tests/live" Component={LiveMonitorOverviewPage} />
            <Route path="scheduled-tests/live" Component={LiveMonitorOverviewPage} />
            <Route
              path="scheduled-tests/results"
              Component={OwnedScheduledTestResultsOverviewPage}
            />
            <Route
              path="scheduled-tests/results/:scheduledTestId/student/:studentId"
              Component={ScheduledTestResultsStudent}
            />
            <Route
              path="scheduled-tests/results/student/:accountId"
              Component={ScheduledTestResultsForAccountPage}
            />
            <Route path="groups" Component={GroupListPage} />
            <Route path="groups/edit/:groupId" Component={GroupDetailsPage} />
            <Route path="product/:productId/teacher-instructions" Component={TeacherInstructions} />
          </Route>

          {/* --- Test environment --- */}
          <Route path={TEST_ENVIRONMENT_ROUTES.LANDING_PAGE} Component={LandingPage} />

          <Route path={TEST_ENVIRONMENT_ROUTES.TEST_RESULTS_PATH}>
            <Route path="" Component={ResultsPage} />
            <Route path=":code" Component={TestResultsPage} />
            <Route path=":code/exercises/:exerciseId" Component={ExerciseResultsPage} />
          </Route>

          <Route path={TEST_ENVIRONMENT_ROUTES.TEST_DETAILS_PAGE} Component={TestPage}>
            <Route index Component={TestEnvPages.TestOverviewPage} />
            <Route
              path="products/:productId/exercises/:exerciseId"
              Component={TestEnvPages.ExercisePage}
            />
            <Route
              path="products/:productId/exercises/:exerciseId/review"
              Component={TestEnvPages.ReviewExercisePage}
            />
            <Route path="completed" Component={TestEnvPages.TestCompletedPage} />
          </Route>

          {/* --- Admin panel --- */}
          <Route
            path={ADMIN_ENVIRONMENT_ROUTES.ROOT_PATH}
            element={
              <RoleComponent
                allowedRoles={[ROLES.ADMIN as ROLE]}
                fallbackContent={<Navigate to={GENERIC_ROUTES.HOME_PAGE} />}
              >
                <AdminPage />
              </RoleComponent>
            }
          >
            <Route path="companies" Component={SchoolsPage} />
            <Route path="companies/:schoolId" Component={SchoolDetailsPage} />
            <Route path="users" Component={UsersPage} />
            <Route path="users/new" Component={NewUserPage} />
            <Route path="users/:userId" Component={UserDetailsPage} />
            <Route path="scheduled-tests" Component={UserScheduledTests} />
            <Route path="scheduled-tests/:date" Component={UserScheduledTestsGroup} />
            <Route path="schools" Component={SchoolsPage} />
            <Route path="schools/:schoolId" Component={SchoolDetailsPage} />
            <Route path="teacher-instructions" Component={TeacherInstructionsAdminPage} />
            <Route path="tools" Component={UserToolsPage} />
          </Route>

          {/* --- Redirectors for legacy urls --- */}
          <Route
            path={LEGACY_ROUTES.TEACHER_DASHBOARD}
            element={<Navigate to={TEACHER_HOME_PAGE_ROUTES.HOME_PAGE} />}
          />
          <Route
            path={LEGACY_ROUTES.STUDENT_DASHBOARD}
            element={<Navigate to={PRODUCT_PAGES_ROUTES.PRODUCT_LIST} />}
          />
          <Route path={LEGACY_ROUTES.STUDENT_DASHBOARD}>
            <Route index element={<Navigate to={PRODUCT_PAGES_ROUTES.PRODUCT_LIST} />} />
            <Route path="inlog" element={<Navigate to={PRODUCT_PAGES_ROUTES.PRODUCT_LIST} />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
};

export default AppRouter;
